import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { authAPI } from '../utils/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Lock, Loader2 } from 'lucide-react';

const INACTIVITY_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

const RequireAuth = ({ children }) => {
  const stored = localStorage.getItem('user');
  const lastActive = parseInt(localStorage.getItem('lastActive') || '0', 10);
  const initialNeedsUnlock = !!stored && !!lastActive && Date.now() - lastActive > INACTIVITY_MS;

  const [user, setUser] = useState(stored ? JSON.parse(stored) : null);
  const [restoring, setRestoring] = useState(!stored);
  const [locked, setLocked] = useState(initialNeedsUnlock);
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);

  // If localStorage was wiped (common on iOS PWAs), try to restore the session from the auth cookie.
  useEffect(() => {
    if (stored) return;
    let active = true;
    authAPI.me()
      .then((res) => {
        if (!active) return;
        const u = res.data.user;
        localStorage.setItem('user', JSON.stringify(u));
        localStorage.setItem('lastActive', Date.now().toString());
        setUser(u);
      })
      .catch(() => { if (active) setUser(null); })
      .finally(() => { if (active) setRestoring(false); });
    return () => { active = false; };
  }, [stored]);

  useEffect(() => {
    if (user && !locked) {
      localStorage.setItem('lastActive', Date.now().toString());
    }
  }, [user, locked]);

  const handleUnlock = async (e) => {
    e?.preventDefault();
    if (!pin) return toast.error('Please enter your PIN');
    setLoading(true);
    try {
      const res = await authAPI.login({ phone_number: user.phone_number, pin });
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      localStorage.setItem('lastActive', Date.now().toString());
      setLocked(false);
      setPin('');
      toast.success('Welcome back!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Incorrect PIN. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try { await authAPI.logout(); } catch { /* ignore */ }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('lastActive');
    window.location.href = '/auth';
  };

  if (restoring) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] to-[#D1FAE5] flex items-center justify-center" data-testid="auth-restoring">
        <Loader2 className="w-8 h-8 text-[#064E3B] animate-spin" />
      </div>
    );
  }

  if (!user) return <Navigate to="/auth" replace />;

  if (locked) {
    const maskedPhone = user.phone_number ? `${user.phone_number.slice(0, 4)}••••${user.phone_number.slice(-2)}` : '';
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] to-[#D1FAE5] flex items-center justify-center px-4" data-testid="pin-lock-screen">
        <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 max-w-sm w-full text-center">
          <div className="w-16 h-16 rounded-2xl bg-[#064E3B] flex items-center justify-center mx-auto mb-6">
            <Lock className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-[#064E3B] mb-1">Welcome back{user.name ? `, ${user.name.split(' ')[0]}` : ''}</h1>
          <p className="text-gray-500 text-sm mb-6">Enter your PIN to continue ({maskedPhone})</p>
          <form onSubmit={handleUnlock} className="space-y-4">
            <div className="text-left">
              <Label htmlFor="unlock-pin">PIN</Label>
              <Input id="unlock-pin" data-testid="unlock-pin-input" type="password" inputMode="numeric" maxLength={6}
                placeholder="••••" value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                className="mt-2 text-center text-2xl tracking-widest" autoFocus />
            </div>
            <Button type="submit" disabled={loading} data-testid="unlock-btn"
              className="w-full bg-[#84CC16] hover:bg-[#65A30D] text-black font-bold py-6 rounded-full">
              {loading ? 'Unlocking...' : 'Unlock'}
            </Button>
          </form>
          <button type="button" onClick={handleLogout} className="text-sm text-gray-500 hover:text-[#064E3B] mt-5" data-testid="not-you-logout">
            Not you? Log out
          </button>
        </div>
      </div>
    );
  }

  return children;
};

export default RequireAuth;
